import type { BottomSheetScrollViewMethods } from '@gorhom/bottom-sheet';
import { useCallback, useEffect, useRef } from 'react';

import {
  isUserPickerRowVisible,
  runUserPickerScrollAttempts,
  userPickerScrollOffset,
} from '@/components/molecules/person-in-charge/userPickerScrollToSelection';

export const useUserPickerScrollToSelection = (
  visible: boolean,
  selectedRowIndex: number | null,
  viewportHeight: number,
) => {
  const scrollRef = useRef<BottomSheetScrollViewMethods>(null);
  const selectedRowYRef = useRef<number | undefined>(undefined);
  const cancelScrollAttemptsRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!visible) {
      selectedRowYRef.current = undefined;
      cancelScrollAttemptsRef.current?.();
    }
  }, [visible]);

  useEffect(() => {
    return () => {
      cancelScrollAttemptsRef.current?.();
    };
  }, []);

  const scrollToSelected = useCallback(() => {
    // Any attempts still queued from an earlier call target a row position that
    // this call is about to re-evaluate, so retire them first either way.
    cancelScrollAttemptsRef.current?.();

    if (selectedRowIndex == null || selectedRowIndex <= 0) {
      return;
    }

    const estimatedY = userPickerScrollOffset(selectedRowIndex);
    if (estimatedY == null) {
      return;
    }

    if (isUserPickerRowVisible(selectedRowYRef.current ?? estimatedY, viewportHeight)) {
      return;
    }

    cancelScrollAttemptsRef.current = runUserPickerScrollAttempts(() => {
      scrollRef.current?.scrollTo({
        y: selectedRowYRef.current ?? estimatedY,
        animated: false,
      });
    });
  }, [selectedRowIndex, viewportHeight]);

  const handleSelectedRowLayout = useCallback(
    (y: number) => {
      selectedRowYRef.current = y;
      scrollToSelected();
    },
    [scrollToSelected],
  );

  const handleSheetChange = useCallback(
    (index: number) => {
      if (index < 0) {
        return;
      }

      scrollToSelected();
    },
    [scrollToSelected],
  );

  return {
    scrollRef,
    scrollToSelected,
    handleSelectedRowLayout,
    handleSheetChange,
  };
};
